import { useTranslation } from "react-i18next";
import Button from "react-bootstrap/esm/Button";
import Card from "react-bootstrap/esm/Card";
import Table from "react-bootstrap/esm/Table";
import Badge from "react-bootstrap/esm/Badge";
import Collapse from "react-bootstrap/esm/Collapse";
import { MonthlyPerformance } from "./types";

interface MonthlyTableProps {
    month: number;
    year: number;
    performances: MonthlyPerformance[];
    isExpanded: boolean;
    onToggleExpanded: () => void;
    onDeletePerformance: (songDocId: string, performanceDocId: string) => void;
    formatDateForDisplay: (date: string) => string;
}

export default function MonthlyTable({
    month,
    year,
    performances,
    isExpanded,
    onToggleExpanded,
    onDeletePerformance,
    formatDateForDisplay,
}: MonthlyTableProps) {
    const { t } = useTranslation();
    const monthName = new Date(year, month - 1, 1).toLocaleDateString(t("calendar.locale") as string, { month: "long" });

    return (
        <Card className="mb-3 shadow-sm border-0">
            <Card.Header
                className="d-flex justify-content-between align-items-center py-3"
                onClick={onToggleExpanded}
                style={{
                    cursor: "pointer",
                    backgroundColor: isExpanded ? "var(--gl-accent-50)" : "var(--gl-surface-raised)",
                    transition: "background-color 0.2s ease",
                }}
            >
                <div className="d-flex align-items-center gap-2">
                    <span style={{ fontWeight: 600, fontSize: "1.1rem" }}>
                        📅 {monthName} {year}
                    </span>
                    <Badge bg="primary" pill>
                        {t("admin.songCount", { count: performances.length })}
                    </Badge>
                </div>
                <span
                    style={{
                        transition: "transform 0.2s ease",
                        transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                >
                    ▼
                </span>
            </Card.Header>
            <Collapse in={isExpanded}>
                <div>
                    <Card.Body className="p-0">
                        <Table striped hover responsive className="mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: "50%" }}>{t("admin.colSong")}</th>
                                    <th style={{ width: "25%" }}>{t("admin.colDate")}</th>
                                    <th style={{ width: "15%" }}>{t("admin.colOccasion")}</th>
                                    <th style={{ width: "10%", textAlign: "center" }}>{t("admin.colAction")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {performances.map((perf) => (
                                    <tr key={`${perf.songDocId}-${perf.performanceDocId}`}>
                                        <td>
                                            <span className="fw-medium">🎵 {perf.songName}</span>
                                        </td>
                                        <td>{formatDateForDisplay(perf.date)}</td>
                                        <td>
                                            {perf.occasion ? (
                                                <Badge bg="secondary">{perf.occasion}</Badge>
                                            ) : (
                                                <span className="text-muted">–</span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDeletePerformance(perf.songDocId, perf.performanceDocId);
                                                }}
                                                className="py-0 px-2"
                                                title={t("admin.deletePerformance") as string}
                                            >
                                                🗑️
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </div>
            </Collapse>
        </Card>
    );
}
